'use server'

import { apiRequest, ApiResponse } from '../request'

export type IntegrationCategory =
    | 'google'
    | 'twilio'
    | 'onesignal'
    | 'daily'
    | 'livekit'
    | 'clicksign'
    | 'focusnfe'
    | 'email'
    | 'openai'
    | 'cloudflare'
    | 'sentry'
    | 'cache'
    | 'app'
    | 'melhorenvio'
    | 'meta'
    | 'whatsapp'
    | 'tiktok'

export type IntegrationCredentialType = 'string' | 'bool' | 'number'

export interface MetaCapiTestResult {
    ok: boolean
    origin?: string
    pixelId?: string
    eventsReceived?: number
    fbtraceId?: string
    error?: string
}

export interface IntegrationCredentialItem {
    key: string
    category: IntegrationCategory
    label: string
    description: string | null
    placeholder: string | null
    isSecret: boolean
    /** Só verify token de webhook: segredo combinado, não de acesso. */
    revealable?: boolean
    type: IntegrationCredentialType
    configured: boolean
    value: string | null
    updatedAt: string | null
}

const BASE = 'master/integrations'

export async function getIntegrationCredentials(): Promise<ApiResponse<IntegrationCredentialItem[]>> {
    return apiRequest<IntegrationCredentialItem[]>(BASE, {})
}

export async function saveIntegrationCredentials(
    items: Array<{ key: string; value: string }>,
): Promise<ApiResponse<any>> {
    return apiRequest<any>(BASE, { data: { items } }, 'post')
}

/** Dispara um Purchase de teste pela CAPI usando as credenciais de plataforma. */
export async function testMetaCapi(): Promise<ApiResponse<MetaCapiTestResult>> {
    return apiRequest<MetaCapiTestResult>('marketing/meta/connection/test-capi', {}, 'post')
}

/**
 * Limpa todo o cache de leitura (Redis) por prefixo, sem afetar as filas.
 * Como o Redis e compartilhado, limpa todas as instancias de uma vez.
 */
export async function clearCache(): Promise<ApiResponse<{ cleared: number }>> {
    return apiRequest<{ cleared: number }>(`${BASE}/cache/clear`, {}, 'post')
}

export interface EmailTestResult {
    ok: boolean
    provider: string
    sentTo: string
    keyPreview: string
    keyLength: number
    keySource: 'painel' | 'env' | 'ausente'
    error?: string
}

/** Envia um email de teste com as credenciais atuais e retorna o erro exato se falhar. */
export async function testEmail(
    to?: string,
): Promise<ApiResponse<EmailTestResult>> {
    return apiRequest<EmailTestResult>(
        `${BASE}/email/test`,
        { data: to ? { to } : {} },
        'post',
    )
}

/**
 * Valor em claro de uma credencial exibível. Só funciona para segredo
 * COMBINADO (verify token de webhook) — segredo de acesso entra e não volta.
 */
export async function revealIntegrationCredential(
    key: string,
): Promise<ApiResponse<{ key: string; value: string }>> {
    return apiRequest<{ key: string; value: string }>(
        `master/integrations/reveal/${encodeURIComponent(key)}`,
        {},
        'get',
    )
}
